/**
 * kd-search-main: v2 検索結果ページ用 Lit コンポーネント
 *
 * sections/v2-search-main.liquid から data-search-terms 属性で初期クエリを受け取り、
 * Algolia の検索結果を Liquid 側コンテナ ([data-search-grid]) に注入する。
 *
 * 設計方針:
 * - kd-algolia-carousel と同じ「cloneNode + applyDisplayDataToFragment」パターンを採用
 *   （HTML 構造は Liquid 側 <template data-hit-template> に集約、Lit は振る舞いのみ）
 * - InstantSearch.js ではなく algoliasearch 単発検索で実装
 *   - 並び替え (sort): <select> の change で sort 用 index suffix を切替て再検索
 *   - 絞り込み (filter): kd-collection-filter の 'kd-collection-filter-apply' イベントを
 *     listen し、:checked checkbox の data-algolia-filter 式を集約して再検索
 *   - pagination: [data-search-pagination] に prev / 数字 / next を描画し、クリックで再検索
 *
 * Algolia 未接続 / 失敗時は Liquid 側の初期描画（v2-collection-product-item の for ループ）
 * をそのまま残す（壊れないフォールバック）。
 */
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { AlgoliaHit } from './algolia-hit-template';
import {
  getAlgoliaConfig,
  createSearchClient,
  waitForAlgoliaLibs,
} from './utils/algolia-client';
import { buildSystemFilters } from './utils/algolia-filters';
import {
  DEFAULT_ATTRIBUTES,
  applyDisplayDataToFragment,
} from './carousel.logic';
import { buildInitialRefinementFilter, resolveCategoryFilterIds } from './search-main.logic';

interface SearchResult {
  hits: AlgoliaHit[];
  nbHits: number;
  nbPages?: number;
  page?: number;
  facets?: Record<string, Record<string, number>>;
}

interface SearchClientLike {
  search: (requests: unknown[]) => Promise<unknown>;
}

/**
 * 商品一覧・検索結果の 1 ページあたりの表示件数。
 *
 * 40 → 80 に変更（課題管理表_phase2,3 #147・業務承認 2026-09-03）。
 * ページ送り 1 回 = Algolia 1 リクエストで、実測 1 日 21,414 リクエスト（全体の 16.7%）が
 * ページ送りによるもの。1 ページの件数を倍にすると送り回数が半分になり全体の -8.3%。
 *
 * 変えるのは 1 画面に並ぶ件数だけで、並び順・絞り込みの仕様は変えない。
 * たどれる商品数の上限も変わらない: Algolia の paginationLimitedTo=1000 が効くため、
 * 40 件 × 25 ページ / 80 件 × 13 ページ のどちらでも到達できるのは先頭 1000 件。
 *
 * この定数はリクエストの hitsPerPage・ページ数の算出・「N件中X件〜Y件を表示」の通し番号の
 * 3 か所で共用しているので、ここだけを変えれば全部そろう。
 */
const DEFAULT_HITS_PER_PAGE = 80;

/**
 * 並び替え label → Algolia index suffix。空文字は base index（売れ筋ランキング順 = popularity）。
 * ラベルは snippets/v2-collection-sort-options.liquid と厳密一致（全角コロン）。
 */
const SORT_LABEL_TO_INDEX_SUFFIX: Record<string, string> = {
  '新着順': '_created_desc',
  '価格：安い順': '_price_with_shipping_asc',
  '価格：高い順': '_price_with_shipping_desc',
  '割引率：大きい順': '_price_ratio_asc',
  '売れ筋ランキング順': '',
  '賞味期限：短い順': '_expiration_asc',
  '賞味期限：長い順': '_expiration_desc',
};

// SORT_LABEL_TO_FILTER は K5-F6 確定（送料系・価格系 sort は完全廃止）により削除。
// 送料系は facet 廃止済み。価格は絞り込みパネル側で対応。
const SORT_LABEL_TO_FILTER: Record<string, string> = {};

// ── 関連度モード（課題#19: 検索の既定を関連度に）──
// 検索（テキストクエリ q あり）でユーザーが並び替え未選択のとき、base index の並びを
// 「人気/日付優先（custom 先頭 = 売れ筋）」から「関連度優先」へ切り替える。
// UI は案B: 並び替え <select> に非表示のプレースホルダ option を挿入して選択し、
// ラベルを「並び替え」表示にする（選択肢として関連度は出さない）。
// コレクション閲覧（q なし）は従来どおり既定=新着順。ユーザーがソートを選べばそのソートに切替。

/** 並び替え <select> のプレースホルダ option の value（実ソート label と衝突しない sentinel）。 */
const RELEVANCE_SORT_VALUE = '__relevance__';
/** プレースホルダ表示ラベル（検索時にドロップダウンへ表示する文言）。 */
const RELEVANCE_SORT_LABEL = '並び替え';
/**
 * 関連度モードで base index に上書き適用する Algolia ranking。
 * 既定（custom 末尾）＝人気/日付より前に words/attribute/exact を評価し、
 * searchableAttributes の優先度（商品名 > カテゴリ > タグ > 説明文）を順位へ効かせる。
 * base index の既定 ranking は custom 先頭（売れ筋）のため、関連度時のみクエリで上書きする。
 */
const RELEVANCE_RANKING: string[] = [
  'typo',
  'geo',
  'words',
  'filters',
  'proximity',
  'attribute',
  'exact',
  'custom',
];

@customElement('kd-search-main')
export class KdSearchMain extends LitElement {
  createRenderRoot() {
    return this;
  }

  // search.terms (Liquid から data-search-terms 属性で渡る)
  @property({ type: String, attribute: 'data-search-terms' })
  searchTerms = '';

  // collection.id (Liquid から data-collection-id 属性で渡る、コレクションページ用)
  // 非カテゴリ collection (キャンペーン等) で collection_ids filter として使う。
  // カテゴリ collection は data-initial-refinement で hierarchicalCategories を使うため collection_ids は不使用。
  @property({ type: String, attribute: 'data-collection-id' })
  collectionId = '';

  // collection.metafields.search_facet.initial.value の JSON 文字列 (§17.3.2)。
  // カテゴリ collection ページで hierarchicalCategories の初期 refinement を設定する。
  // 例: '{"category_lvl0":"食品"}' または '{"category_lvl0":"食品","category_lvl1":"食品 > 肉類"}'
  @property({ type: String, attribute: 'data-initial-refinement' })
  initialRefinement = '';

  private _loaded = false;

  // 現在の並び替え index suffix（'' = 売れ筋ランキング順 / base index）
  private _sortSuffix = '';

  // 関連度モード（検索の既定・課題#19）。true のとき base index に RELEVANCE_RANKING を上書きする。
  // 売れ筋（同じ suffix '' だが custom 先頭のまま）と区別するため suffix とは別に持つ。
  private _isRelevanceMode = false;

  // 並び替えが「filter preset」だった場合の filter 式
  private _sortFilterPreset = '';

  // 「この条件で探す」で適用中の filter 式リスト
  // 各エントリは Algolia filter 式 (expr) と OR 結合用のグループキー (group) の組。
  // 同 group は OR 結合、group 間は AND 結合される（refinementList 相当）。
  // group は data-algolia-filter-group 属性で明示、無ければ式から推定 (NOT 接頭辞を考慮)。
  private _userFilters: Array<{ expr: string; group: string }> = [];

  // kd-collection-filter-apply が発火したかどうか（初期表示と区別するため）。
  // false のときだけ initialRefinement を適用し、true なら filters.length === 0 でも全商品ベースにする。
  private _userHasAppliedFilters = false;

  // 現在のページ番号（0-based）
  private _currentPage = 0;

  // Algolia client は接続成功後に保持して再利用する
  private _searchClient: SearchClientLike | null = null;

  // config も load 中に確定するため保持
  private _config: ReturnType<typeof getAlgoliaConfig> = null;

  private readonly _onPopstate = async (): Promise<void> => {
    this._hydrateFromUrl();
    await this._runSearch({ skipUrlWrite: true });
  };

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    if (this._loaded) return;
    this._loaded = true;
    // UI 同期 (sort 表示 + filter apply 監視) は Algolia 接続有無に関わらず張る
    this._bindSortControls();
    this._bindFilterApply();
    this._setupInitialSort();
    // URL クエリパラメータから初期状態を復元（sort / page / filter）
    // selects と checkboxes を更新、条件タグも再構築してから初回検索を走らせる。
    this._hydrateFromUrl();
    // ブラウザの戻る/進むで URL が変わったら再ハイドレ + 再検索
    window.addEventListener('popstate', this._onPopstate);
    await this._runSearch({ skipUrlWrite: true });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this._onPopstate);
  }

  /**
   * Liquid 描画時に selected 属性が付いた option を読み取り、
   * _sortSuffix / _sortFilterPreset に反映する（再検索は呼ばない）。
   */
  private _applyInitialSortFromDom(): void {
    const select = this.querySelector<HTMLSelectElement>(
      'select[data-search-sort-select-pc], select[data-search-sort-select-sp]',
    );
    if (!select) return;
    const label = select.options[select.selectedIndex]?.textContent?.trim() ?? '';
    this._sortSuffix = SORT_LABEL_TO_INDEX_SUFFIX[label] ?? '';
    this._sortFilterPreset = SORT_LABEL_TO_FILTER[label] ?? '';
  }

  /** 検索文脈（テキストクエリ q あり）かどうか。コレクション閲覧は false。 */
  private _isSearchContext(): boolean {
    return this.searchTerms.trim().length > 0;
  }

  /**
   * 初期ソートを決定する（課題#19）。
   * 検索（q あり）で ?sort= 未指定なら関連度を既定にする（案B）。
   * それ以外（コレクション閲覧 / ?sort= あり）は Liquid の selected option（既定=新着順）を採用。
   * 実際の ?sort= 適用は後続の _hydrateFromUrl が行う。
   */
  private _setupInitialSort(): void {
    const hasUrlSort = new URLSearchParams(window.location.search).has('sort');
    if (this._isSearchContext() && !hasUrlSort) {
      this._enterRelevanceMode();
      return;
    }
    this._applyInitialSortFromDom();
    this._isRelevanceMode = false;
  }

  /**
   * 関連度モード（検索の既定）へ移行する。
   * base index（_sortSuffix=''）を使い、_runSearch で ranking=RELEVANCE_RANKING を上書きする。
   * 並び替え <select> に非表示プレースホルダ option を挿入して選択し、表示ラベルを「並び替え」にする。
   */
  private _enterRelevanceMode(): void {
    this._isRelevanceMode = true;
    this._sortSuffix = '';
    this._sortFilterPreset = '';
    this.querySelectorAll<HTMLSelectElement>(
      'select[data-search-sort-select-pc], select[data-search-sort-select-sp]',
    ).forEach((s) => {
      this._ensureRelevancePlaceholder(s);
      s.value = RELEVANCE_SORT_VALUE;
      const display = s
        .closest('label')
        ?.querySelector<HTMLElement>('[data-search-sort-display]');
      if (display) display.textContent = RELEVANCE_SORT_LABEL;
    });
  }

  /** 並び替え <select> 先頭に非表示のプレースホルダ option（関連度モード用）を1度だけ挿入する。 */
  private _ensureRelevancePlaceholder(select: HTMLSelectElement): void {
    if (Array.from(select.options).some((o) => o.value === RELEVANCE_SORT_VALUE)) {
      return;
    }
    const opt = document.createElement('option');
    opt.value = RELEVANCE_SORT_VALUE;
    opt.textContent = RELEVANCE_SORT_LABEL;
    opt.hidden = true; // ネイティブ一覧には出さない（＝選択肢化しない・案B）
    select.insertBefore(opt, select.firstChild);
  }

  /**
   * Algolia へ問い合わせて [data-search-grid] に hits を流し込む。
   * 初期表示・並び替え変更・絞り込み適用・ページ切替のいずれでも本メソッドを呼び出す。
   * @param options.skipUrlWrite - true のとき URL を書き換えない（履歴復元・戻る/進む時に指定）
   */
  private async _runSearch(options: { skipUrlWrite?: boolean } = {}): Promise<void> {
    const config = this._config ?? getAlgoliaConfig();
    if (!config?.connected) return;
    this._config = config;
    // searchTerms / collectionId / initialRefinement のいずれか一つは必要
    // （すべて無いとコンテキストが無く Liquid フォールバックを残す）
    if (!this.searchTerms && !this.collectionId && !this.initialRefinement) return;

    try {
      await waitForAlgoliaLibs();
      if (!this._searchClient) {
        this._searchClient = createSearchClient({
          cacheKey: 'algolia-responses-search',
          maxEntries: 50,
        });
      }
      if (!this._searchClient) return;

      const systemFilters = buildSystemFilters(config.customer);
      const combinedFilters = this._buildCombinedFilters(systemFilters);
      // facet カウントはユーザーフィルタ未適用の状態で取得する（disjunctive faceting）。
      // userFilters が適用された状態だとチェック中の他選択肢の件数が 0 になってしまうため。
      // B ページ（initialRefinement あり）は全商品ベースにする。
      // カテゴリを変更すると全商品から切り替わる仕様なので facet count も全商品ベースが一貫。
      const facetFilters = this._buildCombinedFilters(systemFilters, [], !!this.initialRefinement);
      const indexName = config.indexName + this._sortSuffix;
      const facetAttrs = [
        'hierarchicalCategories.lvl0',
        'hierarchicalCategories.lvl1',
        // 以下の属性は facetingAfterDistinct だと代表バリアントの値しかカウントされず
        // フィルタ結果の商品数と乖離するため、per-value count クエリ（results[3+]）で取得する。
        //   meta.variant.temperature / dealReasonGroups / price_ratio
        // price はバリアントレベル属性のため per-value count クエリ（results[3+]）で取得する。
        // variants_inventory_count（在庫状況「残りわずか」）は attributesForFaceting 未登録のため
        // facets では取得不可。代わりに numericFilters クエリ（results[2]）で nbHits を件数として使う。
      ];

      // facetingAfterDistinct だと正確な商品件数が取れない属性のプレフィックス一覧。
      // DOM の data-facet-count-key をスキャンし、per-value count クエリを動的生成する。
      const PER_VALUE_PREFIXES = [
        'meta.variant.temperature:',
        'dealReasonGroups:',
        'price_ratio:',
        'price:',
      ];
      const perValueFacetKeys = [
        ...new Set(
          Array.from(this.querySelectorAll<HTMLElement>('[data-facet-count-key]'))
            .map((el) => el.dataset.facetCountKey!)
            .filter((key) => PER_VALUE_PREFIXES.some((p) => key.startsWith(p))),
        ),
      ];

      // facet キーから Algolia filter 式を生成するヘルパー。
      // "attr:value"    → attr:"value"（文字列完全一致）
      // "attr:<=X"      → attr <= X
      // "attr:>=X<=Y"   → attr >= X AND attr <= Y
      const facetKeyToFilterExpr = (key: string): string => {
        const colonIdx = key.indexOf(':');
        const attr = key.slice(0, colonIdx);
        const spec = key.slice(colonIdx + 1);
        if (spec.startsWith('>=') && spec.includes('<=')) {
          const ltIdx = spec.indexOf('<=', 2);
          return `${attr} >= ${spec.slice(2, ltIdx)} AND ${attr} <= ${spec.slice(ltIdx + 2)}`;
        }
        if (spec.startsWith('<=')) return `${attr} <= ${spec.slice(2)}`;
        if (spec.startsWith('>=')) return `${attr} >= ${spec.slice(2)}`;
        return `${attr}:"${spec}"`;
      };

      const response = (await this._searchClient.search([
        {
          indexName,
          params: {
            query: this.searchTerms,
            hitsPerPage: DEFAULT_HITS_PER_PAGE,
            page: this._currentPage,
            distinct: true,
            facetingAfterDistinct: true,
            filters: combinedFilters,
            attributesToRetrieve: DEFAULT_ATTRIBUTES,
            facets: facetAttrs,
            // 関連度モード（検索の既定）: base index の ranking（既定 = custom 先頭 = 売れ筋）を
            // Algolia 既定（custom 末尾）へ上書きし、searchableAttributes の優先度
            // （商品名 > カテゴリ > タグ > 説明文）を順位へ効かせる。
            // facet 系クエリ（results[1+]）は順位非依存のため上書き不要。
            ...(this._isRelevanceMode ? { ranking: RELEVANCE_RANKING } : {}),
          },
        },
        {
          // facet カウント専用クエリ（hits 不要、userFilters なし）
          indexName,
          params: {
            query: this.searchTerms,
            hitsPerPage: 0,
            distinct: true,
            facetingAfterDistinct: true,
            filters: facetFilters,
            facets: facetAttrs,
          },
        },
        {
          // 在庫状況「残りわずか」件数クエリ。
          // 在庫源は product 合計（variants_inventory_count）= カードバッジ（課題ID175）と同一基準。
          // numericFilters は attributesForFaceting 不要で数値フィルタが可能なため、
          // nbHits をそのまま商品件数として使う（facetFilters と同じベースフィルタを適用）。
          indexName,
          params: {
            query: this.searchTerms,
            hitsPerPage: 0,
            distinct: true,
            filters: facetFilters,
            numericFilters: ['variants_inventory_count <= 40'],
          },
        },
        // per-value count クエリ（results[3+]）。
        // 各値の filter を facetFilters に追加して nbHits = 商品数を取得する。
        ...perValueFacetKeys.map((key) => ({
          indexName,
          params: {
            query: this.searchTerms,
            hitsPerPage: 0,
            distinct: true,
            filters: facetFilters
              ? `${facetFilters} AND ${facetKeyToFilterExpr(key)}`
              : facetKeyToFilterExpr(key),
          },
        })),
      ])) as { results: SearchResult[] };

      const result = response.results[0];
      const facetResult = response.results[1];
      const lowStockResult = response.results[2];
      const hits = result?.hits ?? [];
      const nbHits = result?.nbHits ?? 0;
      const nbPages =
        result?.nbPages ?? Math.max(1, Math.ceil(nbHits / DEFAULT_HITS_PER_PAGE));

      const overrideCounts: Record<string, number> = {
        'variants_inventory_count:<=40': lowStockResult?.nbHits ?? 0,
      };
      perValueFacetKeys.forEach((key, i) => {
        overrideCounts[key] = response.results[3 + i]?.nbHits ?? 0;
      });

      this._renderHits(hits);
      this._updateStats(nbHits, hits.length, this._currentPage);
      this._renderPagination(nbPages);
      this._updateFacetCounts(facetResult?.facets ?? {}, overrideCounts);
      this._updateTotalCount(facetResult?.nbHits ?? 0);
      // 成功時に URL を現在の状態に同期 (シェア可能 / リロード時の復元用)
      if (!options.skipUrlWrite) this._writeUrlState();
    } catch {
      // 取得失敗時は Liquid フォールバックのまま
    }
  }

  /**
   * data-facet-count-key 属性を持つ span を Algolia facet 応答で更新する。
   *
   * キー形式:
   *   "attr:value"         → 文字列完全一致（例: meta.variant.temperature:常温）
   *   "attr:<=X"           → 数値上限の累計（例: price:<=1000）
   *   "attr:>=X<=Y"        → 数値範囲の累計（例: price:>=1000<=3000）
   *
   * facets はユーザーフィルタ未適用のクエリ結果を使うため、チェック中の他選択肢も 0 にならない。
   * 属性がレスポンスに含まれない（attributesForFaceting 未設定等）場合も件数 0 として「（0）」を表示する。
   *
   * @param overrideCounts - facets レスポンスでは取得できないキーの件数を直接渡す（例: numericFilters クエリの nbHits）。
   *                         key は data-facet-count-key と同じ形式（例: 'variants_inventory_count:<=40'）。
   */
  private _updateFacetCounts(
    facets: Record<string, Record<string, number>>,
    overrideCounts: Record<string, number> = {},
  ): void {
    this.querySelectorAll<HTMLElement>('[data-facet-count-key]').forEach((el) => {
      const key = el.dataset.facetCountKey ?? '';
      const colonIdx = key.indexOf(':');
      if (colonIdx === -1) return;
      const attr = key.slice(0, colonIdx);
      const valueSpec = key.slice(colonIdx + 1);

      let count: number;
      // overrideCounts が優先（attributesForFaceting 未登録属性の件数を別クエリで補完）
      if (key in overrideCounts) {
        count = overrideCounts[key];
      } else {
        const attrFacets = facets[attr];
        if (attrFacets === undefined) {
          // 属性がレスポンスに含まれない = 件数 0 として扱う
          count = 0;
        } else if (valueSpec.startsWith('>=') && valueSpec.includes('<=')) {
          // 数値範囲: >=X<=Y 形式で X 以上 Y 以下の件数を合計
          const ltIdx = valueSpec.indexOf('<=', 2);
          const min = parseFloat(valueSpec.slice(2, ltIdx));
          const max = parseFloat(valueSpec.slice(ltIdx + 2));
          count = Object.entries(attrFacets).reduce((sum, [k, v]) => {
            const val = parseFloat(k);
            return val >= min && val <= max ? sum + v : sum;
          }, 0);
        } else if (valueSpec.startsWith('<=')) {
          // 数値上限: <=X 以下の件数を合計
          const threshold = parseFloat(valueSpec.slice(2));
          count = Object.entries(attrFacets).reduce(
            (sum, [k, v]) => (parseFloat(k) <= threshold ? sum + v : sum),
            0,
          );
        } else {
          // 文字列完全一致
          count = attrFacets[valueSpec] ?? 0;
        }
      }

      el.textContent = `（${count.toLocaleString('ja-JP')}）`;
      el.classList.remove('tw:hidden');

      // 件数 0 のとき対応する checkbox を disabled にする。
      // B ページの facet も全商品ベースで算出されるため、0 件は本当に存在しない = カテゴリも含めて disabled。
      const label = el.closest('label');
      const checkbox = label?.querySelector<HTMLInputElement>('input[type="checkbox"]');
      if (checkbox) {
        checkbox.disabled = count === 0;
      }
    });
  }

  /**
   * system / collection / sort preset / user 選択の filter 式を Algolia filters 構文に合成する。
   *
   * コレクション filter の適用ルール（仕様 B/C）:
   *   - B（カテゴリ collection）+ ユーザーがカテゴリ未選択  → initialRefinement（hierarchicalCategories）
   *   - B（カテゴリ collection）+ ユーザーがカテゴリ選択済み → なし（全商品から userFilters で OR 検索）
   *   - C（非カテゴリ collection）                          → collection_ids（常に維持）
   *
   * @param userFilters - 省略時は this._userFilters を使用。[] を渡すと userFilters なし（facet 専用クエリ用）
   * @param ignoreInitialRefinement - true のとき initialRefinement を無視する（B ページの全商品ベース facet 用）
   */
  private _buildCombinedFilters(
    systemFilters: string,
    userFilters?: Array<{ expr: string; group: string }>,
    ignoreInitialRefinement = false,
  ): string {
    const filters = userFilters ?? this._userFilters;
    const parts: string[] = [];
    if (systemFilters) parts.push(systemFilters);

    const hasUserCategoryFilter = filters.some(
      ({ group }) => group === 'hierarchicalCategories',
    );

    if (!hasUserCategoryFilter) {
      if (ignoreInitialRefinement) {
        // facet 専用クエリ: B ページは initialRefinement も collection_ids も除く（全商品ベース）
        // C ページ（initialRefinement なし）は collection_ids を維持
        if (!this.initialRefinement && this.collectionId) {
          parts.push(`collection_ids:${this.collectionId}`);
        }
      } else {
        const initialFilter = buildInitialRefinementFilter(this.initialRefinement);
        if (initialFilter && filters.length === 0 && !this._userHasAppliedFilters) {
          // B ページ初期状態（apply 未実行）: collection に留まる
          parts.push(initialFilter);
        } else if (this.collectionId && !this.initialRefinement) {
          // C ページ: collection_ids を維持
          parts.push(`collection_ids:${this.collectionId}`);
        }
        // B ページ + userFilters あり + カテゴリ未選択 → 全商品ベース（何も付けない）
      }
    } else if (!this.initialRefinement && this.collectionId) {
      // C ページ: ユーザーがカテゴリを選択しても collection_ids は維持する
      parts.push(`collection_ids:${this.collectionId}`);
    }
    // B ページ + ユーザーがカテゴリ選択済み → initialFilter も collection_ids も付けない（全商品から）
    if (this._sortFilterPreset) parts.push(`(${this._sortFilterPreset})`);

    if (filters.length > 0) {
      const groups: Record<string, string[]> = {};
      filters.forEach(({ expr, group }) => {
        if (!groups[group]) groups[group] = [];
        groups[group].push(expr);
      });
      Object.values(groups).forEach((g) => {
        if (g.length === 1) {
          parts.push(`(${g[0]})`);
        } else {
          parts.push(`(${g.map((x) => `(${x})`).join(' OR ')})`);
        }
      });
    }
    return parts.join(' AND ');
  }

  /**
   * checkbox から filter エントリ（expr + group）を生成する純関数。
   * 1. data-algolia-filter-group 属性が指定されていればそれを採用
   * 2. 無ければ式の先頭から推定 (NOT 接頭辞を取り除いて ":" / "<" / "=" 前を取る)
   *    例: 'NOT tags:"クール便冷蔵"' → 'tags' (NOT tags ではなく)
   */
  private _filterEntryFromCheckbox(
    cb: HTMLInputElement,
  ): { expr: string; group: string } | null {
    const expr = cb.dataset.algoliaFilter;
    if (!expr) return null;
    const explicit = cb.dataset.algoliaFilterGroup;
    const group =
      explicit ?? expr.replace(/^NOT\s+/, '').split(/[:<>=]/)[0].trim();
    return { expr, group };
  }

  private _renderHits(hits: AlgoliaHit[]): void {
    const tpl = this.querySelector<HTMLTemplateElement>('[data-hit-template]');
    const container = this.querySelector<HTMLElement>('[data-search-grid]');
    if (!tpl || !container) return;

    container.replaceChildren();

    if (hits.length === 0) {
      const emptyP = document.createElement('p');
      emptyP.className =
        'tw:col-span-2 tw:lg:col-span-5 tw:text-center tw:text-text-subdued tw:py-12';
      // コレクションページ (searchTerms 空) では「キーワード」の概念が無いため
      // フィルタ向けメッセージに切替。検索ページは従来通りクエリ文言を出す。
      emptyP.textContent = this.searchTerms
        ? `「${this.searchTerms}」の検索結果はありません。キーワードを変えて再度検索してください。`
        : '該当する商品が見つかりませんでした。絞り込み条件を変えて再度お試しください。';
      container.appendChild(emptyP);
      return;
    }

    hits.forEach((hit, i) => {
      const fragment = tpl.content.cloneNode(true) as DocumentFragment;
      // PLP / 検索は先頭カードが LCP 候補になるため prioritizeFirstImage を渡す
      // （index 0 の画像を eager + fetchpriority=high にする）。
      applyDisplayDataToFragment(fragment, hit, i, {
        showRank: false,
        prioritizeFirstImage: true,
      });
      container.appendChild(fragment);
    });
  }

  /**
   * 件数表記を「N件中 X件〜Y件を表示」フォーマットで PC/SP 両方に反映する。
   */
  private _updateStats(nbHits: number, hitsLength: number, page: number): void {
    let text: string;
    if (nbHits === 0) {
      text = '0件';
    } else {
      const start = page * DEFAULT_HITS_PER_PAGE + 1;
      const end = Math.min(start + hitsLength - 1, nbHits);
      text = `${nbHits}件中${start}件〜${end}件を表示`;
    }
    this.querySelectorAll<HTMLElement>('[data-search-stats]').forEach((el) => {
      el.textContent = text;
    });
    // SP ツールバー 1 行用のコンパクト表記（「N件」）。詳細表記は幅を取るため SP は簡略版を使う。
    const compact = nbHits === 0 ? '0件' : `${nbHits}件`;
    this.querySelectorAll<HTMLElement>('[data-search-stats-compact]').forEach((el) => {
      el.textContent = compact;
    });
  }

  // ── 並び替え / フィルタ ──

  private _bindSortControls(): void {
    const selects = Array.from(
      this.querySelectorAll<HTMLSelectElement>(
        'select[data-search-sort-select-pc], select[data-search-sort-select-sp]',
      ),
    );
    if (selects.length === 0) return;

    const syncDisplaysAndState = (source: HTMLSelectElement): void => {
      const value = source.value;
      const label =
        source.options[source.selectedIndex]?.textContent?.trim() ?? '';
      selects.forEach((s) => {
        if (s.value !== value) s.value = value;
        const display = s
          .closest('label')
          ?.querySelector<HTMLElement>('[data-search-sort-display]');
        if (display) display.textContent = label;
      });
      // ユーザーが実ソートを選択したら関連度モードを解除（プレースホルダは非表示のため通常ここは false）。
      this._isRelevanceMode = value === RELEVANCE_SORT_VALUE;
      this._sortSuffix = SORT_LABEL_TO_INDEX_SUFFIX[label] ?? '';
      this._sortFilterPreset = SORT_LABEL_TO_FILTER[label] ?? '';
    };

    selects.forEach((select) => {
      select.addEventListener('change', async () => {
        syncDisplaysAndState(select);
        // 並び替え変更時はページを 0 に戻す
        this._currentPage = 0;
        await this._runSearch();
        this._scrollToTop();
      });
    });
  }

  private _bindFilterApply(): void {
    this.addEventListener('kd-collection-filter-apply', async (e: Event) => {
      const ev = e as CustomEvent<{ filters: Array<{ expr: string; group: string }>; source?: 'pc' | 'sp' }>;
      let filters = ev.detail?.filters ?? [];
      // B ページの初期チェック（_setInitialCategoryCheck）は PC/SP 両方が checked になっているため、
      // apply 元コンテナ（source）の checkbox 状態を参照して初期カテゴリの維持/除外を判定する。
      // SP apply → SP モーダルの checked を確認。PC apply / 未指定 → PC サイドバーの checked を確認。
      const initialExpr = buildInitialRefinementFilter(this.initialRefinement);
      if (initialExpr) {
        const source = ev.detail?.source;
        const sourceContainerSel =
          source === 'sp'
            ? '[data-collection-filter-modal] input[data-algolia-filter]'
            : '[data-collection-facets] input[data-algolia-filter]';
        const otherContainerSel =
          source === 'sp'
            ? '[data-collection-facets] input[data-algolia-filter]'
            : '[data-collection-filter-modal] input[data-algolia-filter]';
        const isInitialChecked = Array.from(
          this.querySelectorAll<HTMLInputElement>(sourceContainerSel),
        ).some((cb) => cb.dataset.algoliaFilter === initialExpr && cb.checked);
        if (!isInitialChecked) {
          filters = filters.filter((f) => f.expr !== initialExpr);
        }
        // apply 元コンテナの checked 状態を逆コンテナに同期して stale state を防止する。
        // PC で外して apply → SP がまだ checked のままだと次の SP apply で初期カテゴリが復活する。
        this.querySelectorAll<HTMLInputElement>(otherContainerSel).forEach((cb) => {
          if (cb.dataset.algoliaFilter === initialExpr) cb.checked = isInitialChecked;
        });
      }
      this._userFilters = filters;
      this._userHasAppliedFilters = true;
      // 絞り込み適用時もページを 0 に戻す
      this._currentPage = 0;
      await this._runSearch();
      this._scrollToTop();
    });
  }

  /**
   * 絞り込み適用 / ページ切替後にページ最上部までスムーズスクロール。
   * SP モーダルは kd-collection-filter 側で既に閉じている前提。
   */
  private _scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── ページネーション ──

  /**
   * [data-search-pagination] に prev / 数字ボタン / next を描画する。
   * Figma 1122:42495 (PC) / 1122:45065 (SP) 準拠。
   */
  private _renderPagination(nbPages: number): void {
    const nav = this.querySelector<HTMLElement>('[data-search-pagination]');
    if (!nav) return;

    // 1 ページ以下なら何も描画しない（pagination 自体不要）
    if (nbPages <= 1) {
      nav.replaceChildren();
      return;
    }

    const wrap = document.createElement('div');
    wrap.className =
      'tw:flex tw:items-center tw:justify-center tw:gap-[3px] tw:lg:gap-4 tw:h-10';

    // prev
    wrap.appendChild(this._buildPrevNextBtn('prev', this._currentPage));
    // page numbers
    const pageGroup = document.createElement('div');
    pageGroup.className = 'tw:flex tw:items-center tw:gap-[3px] tw:lg:gap-3 tw:h-full';
    this._computePageList(this._currentPage, nbPages).forEach((p) => {
      if (p === 'ellipsis') {
        const dots = document.createElement('span');
        dots.className =
          'tw:w-8 tw:px-0 tw:text-center tw:lg:w-auto tw:lg:px-1 tw:text-text-main tw:text-[15px] tw:leading-6 tw:tracking-[1.5px] tw:select-none';
        dots.textContent = '・・・';
        pageGroup.appendChild(dots);
      } else {
        pageGroup.appendChild(this._buildPageBtn(p, p === this._currentPage));
      }
    });
    wrap.appendChild(pageGroup);
    // next
    wrap.appendChild(this._buildPrevNextBtn('next', this._currentPage, nbPages));

    nav.replaceChildren(wrap);
  }

  /**
   * ページ番号ボタン (アクティブ時は黒背景白文字、非アクティブは白背景ボーダーのみ)。
   */
  private _buildPageBtn(pageIndex: number, isActive: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-label', `${pageIndex + 1}ページ`);
    if (isActive) btn.setAttribute('aria-current', 'page');
    const base =
      'tw:inline-flex tw:items-center tw:justify-center tw:w-9 tw:px-0 tw:lg:w-auto tw:lg:px-4 tw:h-10 tw:py-1 tw:rounded-[4px] tw:text-[15px] tw:leading-6 tw:tracking-[1.5px] tw:cursor-pointer';
    btn.className = isActive
      ? `${base} tw:bg-surface-inverse tw:border tw:border-text-main tw:text-text-inversed tw:font-bold`
      : `${base} tw:bg-surface-main tw:border tw:border-border-default tw:text-text-main tw:font-normal`;
    btn.textContent = String(pageIndex + 1).padStart(2, '0');
    if (!isActive) {
      btn.addEventListener('click', () => this._goToPage(pageIndex));
    }
    return btn;
  }

  /**
   * Prev/Next ボタン (rounded-44 + chevron icon)。
   * direction='prev' は currentPage が 0 なら disabled、'next' は last なら disabled。
   */
  private _buildPrevNextBtn(
    direction: 'prev' | 'next',
    currentPage: number,
    nbPages?: number,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    let disabled = false;
    let targetPage = currentPage;
    if (direction === 'prev') {
      disabled = currentPage <= 0;
      targetPage = currentPage - 1;
      btn.setAttribute('aria-label', '前のページ');
    } else {
      disabled = nbPages !== undefined && currentPage >= nbPages - 1;
      targetPage = currentPage + 1;
      btn.setAttribute('aria-label', '次のページ');
    }
    btn.disabled = disabled;
    const base =
      'tw:inline-flex tw:items-center tw:justify-center tw:w-9 tw:px-0 tw:lg:w-auto tw:lg:px-4 tw:h-10 tw:py-1 tw:rounded-[44px] tw:border tw:border-border-default tw:cursor-pointer';
    btn.className = disabled
      ? `${base} tw:bg-surface-card-sub tw:opacity-50 tw:cursor-not-allowed`
      : `${base} tw:bg-surface-main`;
    // chevron svg (left or right)
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('viewBox', '0 0 7 12');
    svg.setAttribute('width', '7');
    svg.setAttribute('height', '12');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('tw:text-text-main');
    const path = document.createElementNS(svgNs, 'path');
    // direction='prev' → 左向き / 'next' → 右向き
    path.setAttribute('d', direction === 'prev' ? 'M6 1L1 6L6 11' : 'M1 1L6 6L1 11');
    svg.appendChild(path);
    btn.appendChild(svg);
    if (!disabled) {
      btn.addEventListener('click', () => this._goToPage(targetPage));
    }
    return btn;
  }

  /**
   * 表示するページ番号リスト（先頭 + 現在ページ近傍3件 + 末尾）を計算する。
   *
   * 常に「現在ページの前後1ページ」を含む3件の窓を出す。窓は先頭側/末尾側で
   * クランプするため、番号ボタンの数は先頭・窓3件・末尾の計5個で一定になる。
   * 窓と先頭/末尾の間が1ページ以上空くときだけ ellipsis を挟む（隣接しているのに
   * 「・・・」が出て中身が無い、という表示にしない）。
   *
   * 🔴 課題#133: 以前は先頭側だけ [0, 1, 2, ellipsis, last] の固定表示だったため、
   * 3ページ目にいるときに 4ページ目の番号ボタンが出なかった（「次へ」は押せるが、
   * 番号で1つ先へ飛べない）。Figma 1122:42495 の「先頭3 + ... + 末尾」から、
   * 現在ページを中心にした窓へ変更している。戻すと #133 が再発する。
   */
  private _computePageList(
    currentPage: number,
    nbPages: number,
  ): (number | 'ellipsis')[] {
    if (nbPages <= 5) {
      return Array.from({ length: nbPages }, (_, i) => i);
    }
    // 窓の左端。1（先頭の次）〜 nbPages - 4（末尾の3つ手前）にクランプする。
    const windowStart = Math.min(Math.max(currentPage - 1, 1), nbPages - 4);
    const windowEnd = windowStart + 2;
    const list: (number | 'ellipsis')[] = [0];
    if (windowStart > 1) list.push('ellipsis');
    for (let page = windowStart; page <= windowEnd; page += 1) list.push(page);
    if (windowEnd < nbPages - 2) list.push('ellipsis');
    list.push(nbPages - 1);
    return list;
  }

  private async _goToPage(pageIndex: number): Promise<void> {
    if (pageIndex === this._currentPage) return;
    this._currentPage = pageIndex;
    await this._runSearch();
    this._scrollToTop();
  }

  // ── URL 同期（共有 / リロード / 戻る進む対応） ──

  /**
   * 現在の sort / page / filter 状態を URLSearchParams に書き出し、
   * history.replaceState で URL を更新する。
   * デフォルト値（sort=新着順 / page=1 / filter なし）は URL から省略してクリーンに保つ。
   * 既存パラメータ (q 等) は維持する。
   */
  private _writeUrlState(): void {
    const params = new URLSearchParams(window.location.search);

    // sort（文脈依存の既定は URL から省略してクリーンに保つ・課題#19）
    //  - 検索の既定 = 関連度モード → 省略
    //  - コレクションの既定 = 新着順 → 省略
    //  - 検索で新着順を「明示選択」した場合は省略できない（省略＝関連度になるため）→ 明示的に書く
    const select = this.querySelector<HTMLSelectElement>(
      'select[data-search-sort-select-pc], select[data-search-sort-select-sp]',
    );
    if (this._isRelevanceMode) {
      params.delete('sort');
    } else if (select) {
      const label = select.options[select.selectedIndex]?.textContent?.trim() ?? '';
      const isCollectionDefault = !this._isSearchContext() && label === '新着順';
      if (label && label !== RELEVANCE_SORT_LABEL && !isCollectionDefault) {
        params.set('sort', label);
      } else {
        params.delete('sort');
      }
    }

    // page (URL は 1-based)
    if (this._currentPage > 0) {
      params.set('page', String(this._currentPage + 1));
    } else {
      params.delete('page');
    }

    // filter: _userFilters の expr に一致する checkbox の ID のみ URL に書く。
    // _setInitialCategoryCheck で付けた初期チェックは _userFilters に入っていないため除外される。
    const userExprs = new Set(this._userFilters.map((f) => f.expr));
    const ids: string[] = [];
    const seen = new Set<string>();
    if (userExprs.size > 0) {
      this.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-algolia-filter]',
      ).forEach((cb) => {
        if (!userExprs.has(cb.dataset.algoliaFilter ?? '')) return;
        let id = cb.dataset.filterId ?? '';
        // SP 側 (sp- prefix) は PC と同じ base ID に正規化
        if (id.startsWith('sp-')) id = id.slice(3);
        if (id && !seen.has(id)) {
          seen.add(id);
          ids.push(id);
        }
      });
    }
    if (ids.length > 0) {
      params.set('filter', ids.join(','));
    } else {
      params.delete('filter');
    }

    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    // pushState だと履歴が増えすぎるので replaceState で同一エントリ更新
    if (newUrl !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, '', newUrl);
    }
  }

  /**
   * URLSearchParams から状態を読み取り、selects / checkboxes / _currentPage に反映する。
   * 連動して _sortSuffix / _sortFilterPreset / _userFilters も更新し、
   * kd-collection-filter の条件タグも再構築する。
   * （実際の Algolia 検索は呼び元の _runSearch に委ねる）
   */
  private _hydrateFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    // URL の filter= の有無で apply 済みフラグをリセット/セット
    // （戻る/進むで filter が消えた場合も初期状態に戻す）
    this._userHasAppliedFilters = (params.get('filter') ?? '').length > 0;

    // sort（課題#19: 関連度モードと文脈既定を考慮）
    const sortLabel = params.get('sort');
    if (sortLabel && sortLabel in SORT_LABEL_TO_INDEX_SUFFIX) {
      // 明示ソート（?sort= あり・既知ラベル）→ 関連度モードを解除してそのソートを適用
      this._isRelevanceMode = false;
      const selects = this.querySelectorAll<HTMLSelectElement>(
        'select[data-search-sort-select-pc], select[data-search-sort-select-sp]',
      );
      selects.forEach((s) => {
        const optExists = Array.from(s.options).some((o) => o.value === sortLabel);
        if (optExists) {
          s.value = sortLabel;
          const display = s
            .closest('label')
            ?.querySelector<HTMLElement>('[data-search-sort-display]');
          if (display) display.textContent = sortLabel;
        }
      });
      this._sortSuffix = SORT_LABEL_TO_INDEX_SUFFIX[sortLabel] ?? '';
      this._sortFilterPreset = SORT_LABEL_TO_FILTER[sortLabel] ?? '';
    } else if (!sortLabel && this._isSearchContext()) {
      // 検索で ?sort= 無し → 関連度を既定に（初回・戻る/進むの両方に対応）
      this._enterRelevanceMode();
    }
    // 上記以外（未知ラベルの旧ブックマーク / コレクションで ?sort= 無し）は現状維持
    // （_setupInitialSort で設定済みの DOM 初期値 = 新着順 等を保持）

    // page (URL は 1-based、内部は 0-based)。URL に無い時は 0 へリセット
    const pageStr = params.get('page');
    if (pageStr) {
      const p = parseInt(pageStr, 10);
      this._currentPage = !Number.isNaN(p) && p > 0 ? p - 1 : 0;
    } else {
      this._currentPage = 0;
    }

    // filter: popstate（戻る進む）でも完全に追従させるため、
    // まず全 checkbox を解除して _userFilters を空にしてから URL の filter を適用する。
    // （これをしないと URL から filter= が消えても古い :checked が残ったままになる）
    this.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-algolia-filter]',
    ).forEach((cb) => {
      cb.checked = false;
      cb.disabled = false;
    });
    this._userFilters = [];

    // 課題#68: URL に大カテゴリとその配下の中カテゴリが同時に入っている場合は
    // 大カテゴリを落とす（中カテゴリ優先）。checked を直接立てるこの経路は
    // kd-collection-filter の change ハンドラの親子排他を通らないため、
    // ここで解決しないと lvl0 に absorption され中カテゴリで絞れない。
    // availableIds は「いま画面に存在する checkbox の id」。これを渡さないと、
    // 廃止済みの子 id を含む既存URLで親まで落ちて全商品表示になる（子 id は位置ベース）。
    const availableFilterIds = Array.from(
      this.querySelectorAll<HTMLInputElement>('input[data-filter-id]'),
    ).map((el) => (el.dataset.filterId ?? '').replace(/^sp-/, ''));
    const filterIds = resolveCategoryFilterIds(
      (params.get('filter') ?? '').split(',').filter(Boolean),
      availableFilterIds,
    );
    if (filterIds.length === 0 && this.initialRefinement) {
      // URL に filter がなく B ページの場合: initialRefinement に対応するカテゴリを初期チェックにする
      this._setInitialCategoryCheck();
    } else if (filterIds.length > 0) {
      filterIds.forEach((id) => {
        const pcCb = this.querySelector<HTMLInputElement>(
          `input[data-filter-id="${id}"]`,
        );
        const spCb = this.querySelector<HTMLInputElement>(
          `input[data-filter-id="sp-${id}"]`,
        );
        if (pcCb) pcCb.checked = true;
        if (spCb) spCb.checked = true;
      });
      const seen = new Set<string>();
      const filters: Array<{ expr: string; group: string }> = [];
      this.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-algolia-filter]:checked',
      ).forEach((cb) => {
        const entry = this._filterEntryFromCheckbox(cb);
        if (entry && !seen.has(entry.expr)) {
          seen.add(entry.expr);
          filters.push(entry);
        }
      });
      this._userFilters = filters;
    }

    // 条件タグの再構築は filter の有無に関わらず必須
    // （filter が消えた時は空の状態に再構築 = タグも消える）
    const kdFilter = this.querySelector('kd-collection-filter') as
      | (Element & { rebuildAllConditionsFromChecked?: () => void })
      | null;
    kdFilter?.rebuildAllConditionsFromChecked?.();
  }

  /**
   * data-facet-total-count 属性を持つ span を facet クエリの総件数で更新する。
   * 在庫状況「すべて」など filter_expr なしの全件行に使用。
   */
  private _updateTotalCount(nbHits: number): void {
    this.querySelectorAll<HTMLElement>('[data-facet-total-count]').forEach((el) => {
      el.textContent = `（${nbHits.toLocaleString('ja-JP')}）`;
      el.classList.remove('tw:hidden');
    });
  }

  /**
   * B ページ（initialRefinement あり）の初期表示時に、対応するカテゴリ checkbox を checked にする。
   * URL に filter= がない状態で呼ばれる（URL filter がある場合は _hydrateFromUrl の URL 復元が優先）。
   */
  private _setInitialCategoryCheck(): void {
    const filterExpr = buildInitialRefinementFilter(this.initialRefinement);
    if (!filterExpr) return;
    this.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-algolia-filter]',
    ).forEach((cb) => {
      if (cb.dataset.algoliaFilter !== filterExpr) return;
      cb.checked = true;
      // PC サイドバー・SP モーダル内の checkbox なら親 <details> をすべて open にする
      if (cb.closest('[data-collection-facets]') || cb.closest('[data-collection-filter-modal]')) {
        let el: HTMLElement | null = cb.parentElement;
        while (el) {
          if (el instanceof HTMLDetailsElement) el.open = true;
          el = el.parentElement;
        }
      }
    });
  }

  render() {
    return nothing;
  }
}
